import { inject, Injectable } from '@angular/core';
import {
  ConfigBaseFilter,
  ConfigFilterGroup,
  ConfigService,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  PimTemplateProperties,
  SearchField,
  SelectionAction,
  SimpleConfigBaseFilter
} from 'gung-list';
import { OttoOlsenBlockOrder, OttoOlsenBlockOrderAgreementsService, SigningStatus } from './otto-olsen-block-order-agreements.service';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject, forkJoin, map, Observable, of, Subject, switchMap } from 'rxjs';
import { OttoOlsenBlockOrderTableListComponent } from '../components/otto-olsen-block-order-table-list/otto-olsen-block-order-table-list.component';
import { MetadataService } from 'gung-standard';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { gungComparatorHelper } from 'gung-common';
import { OttoOlsenProductAssortmentParentService } from './otto-olsen-product-assortment-parent.service';

export interface GroupedBlockOrderAgreement {
  agreementId: string;
  customerId: string;
  customerName: string;
  offbeskr: string;
  status: string;
  agreementDate: Date;
  agreementValidToDate: Date;
  contact: string;
  rows: OttoOlsenBlockOrder[];
  orderType: number;
  selger?: string;
  signingStatus?: SigningStatus;
  refNumber?: string;
  email1?: string;
  email2?: string;
  agreementTotalValue?: number;
  warehouse?: string;
}

export class OfferDateFilter extends SimpleConfigBaseFilter<GroupedBlockOrderAgreement> {
  type = 'dateRangeFilter';
  typeInDates = true;

  constructor(protected translateService: TranslateService) {
    super();
  }

  getName(): string {
    return 'BLOCK_ORDER_AGREEMENT_DATE';
  }

  getOptionIds(item: GroupedBlockOrderAgreement): string[] {
    let dates = [];
    for (const row of item.rows) {
      if (row.extra.q_gung_ofh?.offertdat) {
        dates.push(format(new Date(row.extra.q_gung_ofh.offertdat), 'yyMMdd'));
      }
    }
    return dates;
  }

  getOptionName(key: string): string {
    return this.translateService.instant(key);
  }
}

export class DateAvtaleFilter extends SimpleConfigBaseFilter<GroupedBlockOrderAgreement> {
  type = 'dateRangeFilter';

  typeInDates = true;

  constructor(protected translateService: TranslateService) {
    super();
  }

  getName(): string {
    return 'BLOCK_ORDER_AGREEMENT_VALID_TO_DATE';
  }

  getOptionIds(item: GroupedBlockOrderAgreement): string[] {
    let dates = [];
    for (const row of item.rows) {
      if (row.extra.q_gung_ofh?.giltigtomdatum) {
        dates.push(format(new Date(row.extra.q_gung_ofh.giltigtomdatum), 'yyMMdd'));
      }
    }
    return dates;
  }

  getOptionName(key: string): string {
    return this.translateService.instant(key);
  }
}

export class BlockOrderTypeFilter extends SimpleConfigBaseFilter<GroupedBlockOrderAgreement> {
  constructor() {
    super();
  }

  getName(): string {
    return 'BLOCK_ORDER_STAUS';
  }

  getOptionIds(item: GroupedBlockOrderAgreement): string[] {
    return [item.status];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class SalesFilter extends SimpleConfigBaseFilter<GroupedBlockOrderAgreement> {
  constructor(protected metadata: MetadataService) {
    super();
  }

  getName(): string {
    return 'SALES';
  }

  getOptionIds(item: GroupedBlockOrderAgreement): string[] {


    return [item.selger];
  }

  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class OttoOlsenBlockOrderAgreementsListConfigService implements ConfigService<GroupedBlockOrderAgreement> {
  protected ottoOlsenProductAssortmentParentService = inject(OttoOlsenProductAssortmentParentService);

  customerId: string;
  topFilter = true;
  selectedColumnSort: Subject<{ field: string; sort: string }> = new BehaviorSubject<{ field: string; sort: string }>(
    null
  );

  constructor(
    protected ottoOlsenBlockOrderAgreementsService: OttoOlsenBlockOrderAgreementsService,
    protected metadataService: MetadataService,
    protected translateService: TranslateService
  ) { }

  getItems(): Observable<GroupedBlockOrderAgreement[]> {
    return this.selectedColumnSort.pipe(
      switchMap(sort => {
        return this.ottoOlsenBlockOrderAgreementsService.getBlockOrders(this.customerId).pipe(
          map(rows => {
            const groupedData = new Map<string, GroupedBlockOrderAgreement>();

            for (const row of rows) {
              const ofh = row.extra?.q_gung_ofh;
              const agreementId = ofh?.offnr;

              if (!agreementId) continue;

              let group = groupedData.get(agreementId);

              if (!group) {
                group = {
                  agreementId,
                  customerId: row.customerId,
                  customerName: ofh.ftgnamn,
                  offbeskr: ofh.offbeskr,
                  agreementDate: new Date(ofh.offertdat),
                  agreementValidToDate: new Date(ofh.giltigtomdatum),
                  agreementTotalValue: 0,
                  status: this.metadataService.getMetadataValue(
                    'xa7',
                    'ordstatbeskr',
                    ofh.ofh_offstatus
                  ),
                  selger: this.metadataService.getMetadataValue(
                    'salj',
                    'saljarenamn',
                    ofh.saljare
                  ),
                  contact: ofh.kundref2,
                  refNumber: ofh.kundbestnr,
                  orderType: ofh.ofh_ordtyp,
                  warehouse: ofh.lagstalle
                    ? this.metadataService.getMetadataValue('xb', 'lagplatsnamn', ofh.lagstalle) || ofh.lagstalle
                    : undefined,
                  signingStatus: row.extra?.signingStatus,
                  rows: []
                };

                groupedData.set(agreementId, group);
              }

              group.rows.push(row);
              group.agreementTotalValue += ofh.vboffradsum || 0;
            }

            const dataToTable = Array.from(groupedData.values());
            this.sortCol(dataToTable, sort);

            return dataToTable;

          }),
          switchMap(data => forkJoin({
            data: of(data),
            productPaths: this.ottoOlsenProductAssortmentParentService.postProductAssortmentParentByProductIds(
              data.map(d => d.rows.map(r => r.extra?.q_gung_ofh?.artnr)).flat().filter(id => !!id)
            )
          })),
          switchMap(({ data, productPaths }) => {
            // Build productId -> path map
            const mappedPaths: Record<string, string> = Object.entries(productPaths).reduce(
              (acc, [productId, assortments]) => {
                const path = `articles/${assortments
                  .slice(1)
                  .map(a => a.id)
                  .join('/')}/${productId}`;

                acc[productId] = path;
                return acc;
              },
              {} as Record<string, string>
            );

            // Assign paths to rows
            for (const d of data) {
              for (const row of d.rows) {
                const artnr = row.extra?.q_gung_ofh?.artnr;
                if (!artnr) continue;

                row.extra._pathToDetails =
                  mappedPaths[artnr] ?? `product/${artnr}`;
              }
            }

            return of(data);

          })
        );
      })
    );
  }

  sortCol(data: GroupedBlockOrderAgreement[], sort: { field: string; sort: string }) {
    if (sort) {
      data.sort((a, b) => {
        const sortType = sort.sort === 'asc' ? 1 : -1;
        return gungComparatorHelper(a[sort.field], b[sort.field], sortType);
      });
    }
    return data;
  }

  getFilters(): ConfigBaseFilter<GroupedBlockOrderAgreement>[] {
    return [
      new OfferDateFilter(this.translateService),
      new DateAvtaleFilter(this.translateService),
      new BlockOrderTypeFilter(),
      new SalesFilter(this.metadataService)
    ];
  }
  getSortOptions(): ListSortOption<GroupedBlockOrderAgreement>[] {
    return [];
  }
  getBatchSizes(): number[] | undefined {
    return [300];
  }
  getLayouts(): ListLayout<GroupedBlockOrderAgreement>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => OttoOlsenBlockOrderTableListComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'blockorder'
      }
    ];
  }

  getSearchTerms(item: GroupedBlockOrderAgreement): string[] {
    return [item.customerId, item.agreementId, item.customerName];
  }

  getItemId(item: GroupedBlockOrderAgreement): string {
    return item.agreementId;
  }
}
